import { useEffect, useRef, useState } from 'react';
import { CalendarClock, Loader2, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { paymentsAPI, usersAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { isAdminRole, isFacilitatorRole } from '../utils/roles';
import HorizontalScroll from './HorizontalScroll';
import type {
  ChargeHistoryItem,
  ChargeHistoryStatus,
  CoverageMonth,
  MemberBillingInfo,
  ScheduledCharge,
} from '../types';

const STAFF_ROLES = new Set(['facilitator', 'admin', 'god_mode']);

export function staffBillingNote(reason?: string | null, fallback = 'No dues'): string {
  return reason?.trim() || fallback;
}

export function coveragePauseLine(reason?: string | null): string {
  const note = staffBillingNote(reason);
  return note === 'No dues' ? 'Suspended — no dues' : `Suspended — ${note}`;
}

export function coverageSkipLine(reason?: string | null): string {
  const note = staffBillingNote(reason);
  return note === 'No dues' ? 'Skipped — no dues' : `Skipped — ${note}`;
}

function isZeroDues(amount: string | null | undefined): boolean {
  if (amount == null || amount === '') return false;
  const n = Number(amount);
  return Number.isFinite(n) && n === 0;
}

const CHARGE_STATUS_BADGE: Record<ScheduledCharge['status'], string> = {
  scheduled: 'bg-blue-50 text-blue-700',
  charged: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-600',
  canceled: 'bg-gray-100 text-gray-500',
};

const HISTORY_STATUS_BADGE: Record<ChargeHistoryStatus, string> = {
  paid: 'bg-green-50 text-green-700',
  open: 'bg-blue-50 text-blue-700',
  failed: 'bg-red-50 text-red-600',
  uncollectible: 'bg-red-50 text-red-600',
  void: 'bg-gray-100 text-gray-500',
};

const HISTORY_STATUS_LABEL: Record<ChargeHistoryStatus, string> = {
  paid: 'Paid',
  open: 'Open',
  failed: 'Failed',
  uncollectible: 'Uncollectible',
  void: 'Void',
};

function formatCardOnFile(info: MemberBillingInfo): string {
  if (!info.has_card) return 'No';
  if (info.card_brand && info.card_last4) {
    return `Yes · ${info.card_brand} ···· ${info.card_last4}`;
  }
  return 'Yes';
}

function formatLastPaidLine(at: string | null, amount: string | null): string {
  if (!at) return '—';
  const date = new Date(at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return amount ? `$${amount} · ${date}` : date;
}

export function formatChargeDate(value: string): string {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = dateOnly ? new Date(`${value}T12:00:00`) : new Date(value);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

type UpcomingCharge = { date: Date; amount: string | null };

export function earliestUpcomingCharge(info: {
  is_paused: boolean;
  next_charge_date: string | null;
  default_amount?: string | null;
  monthly_amount?: string | null;
  scheduled_charges: ScheduledCharge[];
}): UpcomingCharge | null {
  if (info.is_paused) return null;
  const candidates: UpcomingCharge[] = [];
  if (info.next_charge_date) {
    candidates.push({
      date: new Date(info.next_charge_date),
      amount: info.default_amount ?? info.monthly_amount ?? null,
    });
  }
  for (const charge of info.scheduled_charges) {
    if (charge.status !== 'scheduled') continue;
    candidates.push({
      date: new Date(`${charge.charge_on}T12:00:00`),
      amount: charge.amount,
    });
  }
  if (candidates.length === 0) return null;
  return candidates.reduce((soonest, next) =>
    next.date < soonest.date ? next : soonest,
  );
}

function formatUpcomingChargeDate(value: Date): string {
  return value.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

type ChargeRange = 'upcoming' | 'history';
type ChargeTypeFilter = 'all' | 'Monthly dues' | 'One-time';

type LedgerRow = {
  key: string;
  sort: number;
  dateLabel: string;
  amount: string | null;
  type: string;
  status: string;
  statusClass: string;
  note: string;
  bucket: ChargeRange;
  cancelId?: number;
  error?: string;
};

function sixMonthsFromNow(): number {
  const end = new Date();
  end.setMonth(end.getMonth() + 6);
  end.setHours(23, 59, 59, 999);
  return end.getTime();
}

function historyMatchesScheduled(
  history: ChargeHistoryItem[],
  charge: ScheduledCharge,
): boolean {
  const chargeDay = formatChargeDate(charge.charge_on);
  return history.some(
    (item) =>
      item.amount === charge.amount && formatChargeDate(item.date) === chargeDay,
  );
}

function yearMonth(value: string): string {
  if (/^\d{4}-\d{2}/.test(value)) return value.slice(0, 7);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function paidPeriodFromHistory(
  info: MemberBillingInfo,
  month: string,
): ChargeHistoryItem | null {
  const matches = (info.charges ?? []).filter(
    (item) =>
      item.kind === 'monthly_dues' &&
      yearMonth(item.date) === month &&
      (item.status === 'paid' || item.status === 'open'),
  );
  matches.sort((a, b) => {
    const rank = (status: ChargeHistoryStatus) => (status === 'paid' ? 2 : 1);
    const byStatus = rank(b.status) - rank(a.status);
    if (byStatus) return byStatus;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
  if (matches[0]) return matches[0];
  if (
    info.last_paid_at &&
    yearMonth(info.last_paid_at) === month
  ) {
    return {
      id: `local-paid-${month}`,
      date: info.last_paid_at,
      amount: info.last_paid_amount,
      kind: 'monthly_dues',
      description: '',
      status: 'paid',
      covers_processing_fee: false,
    };
  }
  return null;
}

function buildLedgerRows(info: MemberBillingInfo): LedgerRow[] {
  const rows: LedgerRow[] = [];
  const history = info.charges ?? [];

  for (const charge of history) {
    const extra =
      charge.kind === 'monthly_dues' && charge.description
        ? charge.description
        : charge.kind === 'one_time'
          ? charge.description
          : '';
    rows.push({
      key: `hist-${charge.id}`,
      sort: new Date(charge.date).getTime(),
      dateLabel: formatChargeDate(charge.date),
      amount: charge.amount,
      type: charge.kind === 'monthly_dues' ? 'Monthly dues' : 'One-time',
      status: HISTORY_STATUS_LABEL[charge.status],
      statusClass: HISTORY_STATUS_BADGE[charge.status],
      bucket: 'history',
      note: [
        extra,
        charge.covers_processing_fee ? 'Card fee on receipt' : '',
        charge.refunded_amount ? `Refunded $${charge.refunded_amount}` : '',
      ]
        .filter(Boolean)
        .join(' · '),
    });
  }

  for (const charge of info.scheduled_charges) {
    if (charge.status === 'charged' && historyMatchesScheduled(history, charge)) {
      continue;
    }
    rows.push({
      key: `sched-${charge.id}`,
      sort: new Date(`${charge.charge_on}T12:00:00`).getTime(),
      dateLabel: formatChargeDate(charge.charge_on),
      amount: charge.amount,
      type: 'One-time',
      status: charge.status.charAt(0).toUpperCase() + charge.status.slice(1),
      statusClass: CHARGE_STATUS_BADGE[charge.status],
      bucket: charge.status === 'scheduled' ? 'upcoming' : 'history',
      note: charge.description,
      cancelId: charge.status === 'scheduled' ? charge.id : undefined,
      error: charge.status === 'failed' ? charge.error : '',
    });
  }

  for (const month of info.coverage) {
    if (month.subscription_amount && month.subscription_on) {
      rows.push({
        key: `sub-${month.month}`,
        sort: new Date(`${month.subscription_on}T12:00:00`).getTime(),
        dateLabel: formatChargeDate(month.subscription_on),
        amount: month.subscription_amount,
        type: 'Monthly dues',
        status: 'Upcoming',
        statusClass: 'bg-blue-50 text-blue-700',
        bucket: 'upcoming',
        note: '',
      });
    } else if (month.paid_period) {
      const paid = paidPeriodFromHistory(info, month.month);
      if (paid) {
        rows.push({
          key: `cov-${month.month}`,
          sort: new Date(paid.date).getTime(),
          dateLabel: formatChargeDate(paid.date),
          amount: paid.amount,
          type: 'Monthly dues',
          status: HISTORY_STATUS_LABEL[paid.status],
          statusClass: HISTORY_STATUS_BADGE[paid.status],
          bucket: 'upcoming',
          note: [
            paid.description,
            paid.covers_processing_fee ? 'Card fee on receipt' : '',
            paid.refunded_amount ? `Refunded $${paid.refunded_amount}` : '',
          ]
            .filter(Boolean)
            .join(' · '),
        });
      } else {
        rows.push({
          key: `cov-${month.month}`,
          sort: new Date(`${month.month}-01T12:00:00`).getTime(),
          dateLabel: month.label,
          amount: info.default_amount,
          type: 'Monthly dues',
          status: 'Covered',
          statusClass: 'bg-green-50 text-green-700',
          bucket: 'upcoming',
          note: 'Current billing period',
        });
      }
    } else if (month.past_due) {
      rows.push({
        key: `due-${month.month}`,
        sort: new Date(`${month.month}-01T12:00:00`).getTime(),
        dateLabel: month.label,
        amount: info.default_amount,
        type: 'Monthly dues',
        status: 'Past due',
        statusClass: 'bg-red-50 text-red-700',
        bucket: 'upcoming',
        note: 'Card declined — Stripe is retrying',
      });
    } else if (month.paused) {
      rows.push({
        key: `pause-${month.month}`,
        sort: new Date(`${month.month}-01T12:00:00`).getTime(),
        dateLabel: month.label,
        amount: null,
        type: 'Monthly dues',
        status: 'Suspended',
        statusClass: 'bg-yellow-100 text-yellow-700',
        bucket: 'upcoming',
        note: staffBillingNote(info.pause_reason),
      });
    } else if (month.skipped) {
      rows.push({
        key: `skip-${month.month}`,
        sort: new Date(`${month.month}-01T12:00:00`).getTime(),
        dateLabel: month.label,
        amount: null,
        type: 'Monthly dues',
        status: 'Skipped',
        statusClass: 'bg-amber-50 text-amber-700',
        bucket: 'upcoming',
        note: staffBillingNote(info.active_skip?.reason),
      });
    } else if (month.uncovered) {
      rows.push({
        key: `uncov-${month.month}`,
        sort: new Date(`${month.month}-01T12:00:00`).getTime(),
        dateLabel: month.label,
        amount: null,
        type: 'Monthly dues',
        status: 'Uncovered',
        statusClass: 'bg-amber-50 text-amber-700',
        bucket: 'upcoming',
        note: 'Nothing scheduled',
      });
    }
  }

  rows.sort((a, b) => a.sort - b.sort);
  return rows;
}

export function CoverageTimeline({
  months,
  pauseReason,
  skipReason,
}: {
  months: CoverageMonth[];
  pauseReason?: string;
  skipReason?: string;
}) {
  if (months.length === 0) return null;
  return (
    <div className="rounded-lg border border-gray-100 divide-y divide-gray-50 text-sm">
      {months.map((m) => (
        <div key={m.month} className="flex items-start gap-3 px-3 py-1.5">
          <span className="w-20 shrink-0 font-medium text-navy">{m.label}</span>
          <div className="min-w-0 flex-1">
            {m.subscription_amount && (
              <p className="text-gray-700">
                ${m.subscription_amount} subscription
                {m.subscription_on ? ` · ${formatChargeDate(m.subscription_on)}` : ''}
              </p>
            )}
            {m.paid_period && (
              <p className="text-green-700">Covered — current billing period</p>
            )}
            {m.past_due && (
              <p className="text-red-700 font-medium">Past due — card declined, Stripe is retrying</p>
            )}
            {m.one_time.map((c) => (
              <p key={c.id} className={c.status === 'charged' ? 'text-green-700' : 'text-gray-700'}>
                ${c.amount} one-time · {formatChargeDate(c.charge_on)}
                {c.status === 'charged' ? ' (charged)' : ''}
              </p>
            ))}
            {m.paused && (
              <p className="text-yellow-700">{coveragePauseLine(pauseReason)}</p>
            )}
            {m.skipped && !m.paused && (
              <p className="text-amber-600">{coverageSkipLine(skipReason)}</p>
            )}
            {m.uncovered && !m.skipped && !m.paused && (
              <p className="text-amber-600 font-medium">No dues — nothing scheduled</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function renewalMonthLabel(nextChargeIso: string, offset: number): string {
  const d = new Date(nextChargeIso);
  return new Date(d.getFullYear(), d.getMonth() + offset, 1).toLocaleDateString(
    undefined,
    { month: 'short', year: 'numeric' },
  );
}

export function MemberBillingSection({
  memberId,
  role,
  monthlyAmount,
  onChanged,
  canManage = true,
  hideHeading = false,
  className = 'border-t border-gray-100 pt-4 space-y-4',
}: {
  memberId: number;
  role?: string;
  monthlyAmount?: string | null;
  onChanged?: () => void;
  canManage?: boolean;
  hideHeading?: boolean;
  className?: string;
}) {
  const { user } = useAuth();
  const allowManage =
    canManage || isAdminRole(user?.role) || isFacilitatorRole(user?.role);
  const [info, setInfo] = useState<MemberBillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [moveDate, setMoveDate] = useState('');
  const [skipMonths, setSkipMonths] = useState(0);
  const [billingReason, setBillingReason] = useState('');
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [chargeDate, setChargeDate] = useState('');
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeNote, setChargeNote] = useState('');
  const [chargeRange, setChargeRange] = useState<ChargeRange>('upcoming');
  const [chargeType, setChargeType] = useState<ChargeTypeFilter>('all');
  const [chargeStatus, setChargeStatus] = useState('all');
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    paymentsAPI
      .getMemberBilling(memberId)
      .then((data) => {
        if (cancelled) return;
        setInfo(data);
        setChargeAmount(data.default_amount || '');
        if (data.next_charge_date) {
          setMoveDate(data.next_charge_date.slice(0, 10));
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setBillingError('Could not load billing details.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  useEffect(() => {
    if (!billingError) return;
    errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [billingError]);

  const apiErrorMessage = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
    fallback;

  const handleMoveDate = async () => {
    if (!moveDate) return;
    setBusy(true);
    setBillingError(null);
    try {
      const data = await paymentsAPI.setMemberNextChargeDate(memberId, moveDate);
      setInfo(data);
      setMoveDate('');
      toast.success('Subscription renewal date updated.');
      onChanged?.();
    } catch (err) {
      setBillingError(apiErrorMessage(err, 'Could not move the charge date.'));
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    if (skipMonths < 1) return;
    const reason = billingReason.trim();
    if (!reason) {
      setBillingError('Say why in one line so the next person knows.');
      return;
    }
    setBusy(true);
    setBillingError(null);
    try {
      const data = await paymentsAPI.skipMemberBilling(memberId, skipMonths, reason);
      setBillingReason('');
      setInfo(data);
      setSkipMonths(0);
      toast.success(
        `Skipping ${skipMonths} renewal${skipMonths > 1 ? 's' : ''} — billing resumes automatically.`,
      );
      onChanged?.();
    } catch (err) {
      setBillingError(apiErrorMessage(err, 'Could not skip billing.'));
    } finally {
      setBusy(false);
    }
  };

  const handleClearSkip = async () => {
    setBusy(true);
    setBillingError(null);
    try {
      const data = await paymentsAPI.clearMemberBillingSkip(memberId);
      setInfo(data);
      toast.success('Skip cleared — the original renewal date is restored.');
      onChanged?.();
    } catch (err) {
      setBillingError(apiErrorMessage(err, 'Could not clear the skip.'));
    } finally {
      setBusy(false);
    }
  };

  const handleProcessingFee = async (cover: boolean) => {
    const fee = info?.processing_fee_price ? ` (${info.processing_fee_price})` : '';
    if (
      !window.confirm(
        cover
          ? `Add the 3% card fee${fee} to their dues? It starts with their next bill, and they get a notice in the app.`
          : `Remove the 3% card fee${fee} from their dues? It stops with their next bill, and they get a notice in the app.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setBillingError(null);
    try {
      const data = await paymentsAPI.setMemberProcessingFee(memberId, cover);
      setInfo(data);
      toast.success(cover ? 'Card fee added from the next bill.' : 'Card fee removed from the next bill.');
      onChanged?.();
    } catch (err) {
      setBillingError(apiErrorMessage(err, 'Could not change the card fee.'));
    } finally {
      setBusy(false);
    }
  };

  const handleSendUpdateCard = async () => {
    if (
      !window.confirm(
        'Send this member an email to update their card? They will open Settings → Membership & Billing and tap Update credit card.',
      )
    ) {
      return;
    }
    setBusy(true);
    setBillingError(null);
    try {
      await paymentsAPI.sendUpdateCardEmail(memberId);
      toast.success('Update-card email sent.');
    } catch (err) {
      setBillingError(apiErrorMessage(err, 'Could not send the update-card email.'));
    } finally {
      setBusy(false);
    }
  };

  const handleSuspend = async (suspend: boolean, limitAppAccess?: boolean) => {
    if (suspend && limitAppAccess === undefined) return;
    const reason = billingReason.trim();
    if (suspend && !reason) {
      setBillingError('Say why in one line so the next person knows.');
      return;
    }
    setBusy(true);
    setBillingError(null);
    try {
      await usersAPI.manageMember(
        memberId,
        suspend
          ? {
              paused: true,
              limit_app_access: Boolean(limitAppAccess),
              pause_reason: reason,
            }
          : { paused: false },
      );
      setBillingReason('');
      const data = await paymentsAPI.getMemberBilling(memberId);
      setInfo(data);
      toast.success(
        suspend
          ? (limitAppAccess ? 'Billing paused and access limited.' : 'Billing paused.')
          : 'Billing resumed.',
      );
      onChanged?.();
    } catch (err) {
      setBillingError(apiErrorMessage(err, 'Could not update billing.'));
    } finally {
      setBusy(false);
    }
  };

  const handleScheduleCharge = async () => {
    const amount = Number(chargeAmount);
    if (!chargeDate || !Number.isFinite(amount) || amount <= 0) {
      setBillingError('Enter a future date and a positive amount.');
      return;
    }
    setBusy(true);
    setBillingError(null);
    try {
      const charge = await paymentsAPI.scheduleMemberCharge(memberId, {
        amount: amount.toFixed(2),
        charge_on: chargeDate,
        description: chargeNote.trim() || undefined,
      });
      const data = await paymentsAPI.getMemberBilling(memberId);
      setInfo(data);
      setShowChargeForm(false);
      setChargeDate('');
      setChargeNote('');
      toast.success(
        charge.status === 'charged'
          ? 'Charged their card.'
          : charge.status === 'failed'
            ? 'Charge attempted but failed — see the error below.'
            : 'One-time charge scheduled.',
      );
      onChanged?.();
    } catch (err) {
      setBillingError(apiErrorMessage(err, 'Could not schedule the charge.'));
    } finally {
      setBusy(false);
    }
  };

  const handleCancelCharge = async (chargeId: number) => {
    setBusy(true);
    setBillingError(null);
    try {
      await paymentsAPI.cancelScheduledCharge(chargeId);
      const data = await paymentsAPI.getMemberBilling(memberId);
      setInfo(data);
      onChanged?.();
    } catch (err) {
      setBillingError(apiErrorMessage(err, 'Could not cancel the charge.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className={`${className} flex items-center gap-2 text-sm text-gray-500`}>
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading billing…
      </div>
    );
  }
  if (!info) {
    return (
      <div className={`${className} text-sm text-red-600`}>
        {billingError || 'Could not load billing details.'}
      </div>
    );
  }

  const ledgerRows = buildLedgerRows(info);
  const nextCharge = earliestUpcomingCharge(info);
  const upcomingHorizon = sixMonthsFromNow();
  const rangedRows = ledgerRows.filter((row) => {
    if (row.bucket !== chargeRange) return false;
    if (chargeRange === 'upcoming' && row.sort > upcomingHorizon) return false;
    return true;
  });
  const statusOptions = [...new Set(rangedRows.map((row) => row.status))].sort();
  const visibleRows = rangedRows.filter((row) => {
    if (chargeType !== 'all' && row.type !== chargeType) return false;
    if (chargeStatus !== 'all' && row.status !== chargeStatus) return false;
    return true;
  });
  const historyCount = ledgerRows.filter((row) => row.bucket === 'history').length;

  return (
    <div className={className}>
      {!hideHeading && (
        <h4 className="text-sm font-semibold text-navy flex items-center gap-2">
          <CalendarClock size={16} />
          Billing
        </h4>
      )}

      {billingError && (
        <div
          ref={errorRef}
          className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm"
        >
          {billingError}
        </div>
      )}
      {info.stripe_warning && (
        <div className="bg-amber-50 text-amber-800 px-3 py-2 rounded-lg text-sm">
          {info.stripe_warning}
        </div>
      )}

      <div className="space-y-1">
        {info.cancel_at_period_end && info.payment_status !== 'canceled' && (
          <p>
            <span
              className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-800 text-white"
              title="Canceled in Stripe. They keep access until the period ends."
            >
              Leaving
            </span>
          </p>
        )}
        <p className="text-sm text-gray-700">
          {info.cancel_at_period_end && !info.is_paused && info.payment_status !== 'canceled'
            ? 'Access ends: '
            : 'Next charge: '}
          <span className="font-medium text-navy">
            {info.is_paused
              ? 'Paused'
              : nextCharge
                ? `${formatUpcomingChargeDate(nextCharge.date)}${
                    nextCharge.amount && !(info.cancel_at_period_end && info.payment_status !== 'canceled')
                      ? ` · $${nextCharge.amount}`
                      : ''
                  }`
                : '—'}
          </span>
        </p>
        <p className="text-sm text-gray-700">
          Last paid:{' '}
          <span className="font-medium text-navy">
            {formatLastPaidLine(info.last_paid_at, info.last_paid_amount)}
          </span>
        </p>
        <p className="text-sm text-gray-700">
          Card on file:{' '}
          <span className="font-medium text-navy">
            {formatCardOnFile(info)}
          </span>
        </p>
        {allowManage &&
          (info.has_card ||
            info.has_subscription ||
            info.payment_status === 'past_due' ||
            info.payment_status === 'failed') && (
            <div className="pt-1">
              {(info.payment_status === 'past_due' ||
                info.payment_status === 'failed') && (
                <p className="text-sm text-red-600 mb-1.5">
                  Their last charge failed. Send them an email to update the card
                  in Settings.
                </p>
              )}
              <button
                type="button"
                onClick={handleSendUpdateCard}
                disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
              >
                <Send size={12} />
                {busy ? 'Sending…' : 'Send update-card email'}
              </button>
            </div>
          )}
      </div>

      {info.has_subscription ? (
        <div className="space-y-3">
          <div>
            <p className="text-sm text-gray-700">
              Subscription renewal:{' '}
              <span className="font-medium text-navy">
                {info.next_charge_date
                  ? formatUpcomingChargeDate(new Date(info.next_charge_date))
                  : 'Unknown'}
              </span>
            </p>
          </div>

          {allowManage && (
          <div className="rounded-lg border border-gray-200 bg-off-white/60 px-3 py-3 space-y-3">
            <p className="text-sm font-semibold text-navy">Change billing</p>
            {isAdminRole(user?.role) && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1.5">Card processing fee</p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-700">
                    {info.covers_processing_fee
                      ? `They pay the 3% card fee${info.processing_fee_price ? ` (${info.processing_fee_price})` : ''}.`
                      : 'They don’t pay the card fee.'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleProcessingFee(!info.covers_processing_fee)}
                    disabled={busy}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition disabled:opacity-50"
                  >
                    {info.covers_processing_fee ? 'Remove card fee' : 'Add card fee'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Starts with their next bill. They can also change this in Settings.
                </p>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1.5">
                Move subscription renewal
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={moveDate}
                  onChange={(e) => setMoveDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
                  aria-label="New subscription renewal date"
                />
                <button
                  type="button"
                  onClick={handleMoveDate}
                  disabled={busy || !moveDate}
                  className="px-3 py-1.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition disabled:opacity-50"
                >
                  Move
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Moves their monthly renewal — not one-time charges. Nothing
                from the subscription is billed until the new date, then a
                full month starts.
              </p>
            </div>
            <div>
            <p className="text-sm font-medium text-gray-700 mb-1.5">
              Skip or suspend billing
            </p>
            {info.is_paused ? (
              <div className="rounded-lg bg-yellow-50 px-3 py-2.5 flex items-center justify-between gap-3">
                <p className="text-sm text-yellow-700">
                  Billing is paused — nothing will be charged until you resume.
                  {info.limit_app_access
                    ? ' App access is also limited to guest-only surfaces.'
                    : ' They still have full app access.'}
                  {info.pause_reason ? ` Reason: ${info.pause_reason}` : ''}
                </p>
                <button
                  type="button"
                  onClick={() => handleSuspend(false)}
                  disabled={busy}
                  className="px-3 py-1.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition disabled:opacity-50 shrink-0"
                >
                  Resume billing
                </button>
              </div>
            ) : info.active_skip ? (
              <div className="rounded-lg bg-blue-50 px-3 py-2.5 flex items-center justify-between gap-3">
                <p className="text-sm text-blue-700">
                  Skipping {info.active_skip.months} renewal
                  {info.active_skip.months > 1 ? 's' : ''} — billing resumes{' '}
                  {formatUpcomingChargeDate(new Date(info.active_skip.resume_on))}
                  .
                  {info.active_skip.reason ? ` Reason: ${info.active_skip.reason}` : ''}
                  {' '}To pick different months, clear this skip first.
                </p>
                <button
                  type="button"
                  onClick={handleClearSkip}
                  disabled={busy}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-red-600 border border-gray-300 rounded-lg transition disabled:opacity-50 shrink-0"
                >
                  Clear skip
                </button>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-1.5">
                  {info.next_charge_date &&
                    [1, 2, 3, 4, 5, 6].map((k) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setSkipMonths(skipMonths === k ? 0 : k)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                          k <= skipMonths
                            ? 'bg-navy text-white border-navy'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-navy'
                        }`}
                      >
                        {renewalMonthLabel(info.next_charge_date as string, k - 1)}
                      </button>
                    ))}
                  {skipMonths > 0 && (
                    <button
                      type="button"
                      onClick={handleSkip}
                      disabled={busy || !billingReason.trim()}
                      className="ml-1 px-3 py-1.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      Skip {skipMonths} month{skipMonths > 1 ? 's' : ''}
                    </button>
                  )}
                </div>
                <label className="block mt-2">
                  <span className="sr-only">Why you are skipping or pausing</span>
                  <input
                    type="text"
                    value={billingReason}
                    onChange={(e) => setBillingReason(e.target.value)}
                    maxLength={200}
                    placeholder="Why? One line — the next facilitator will see this."
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Pick the subscription months to skip (consecutive, starting
                  with their next renewal) — billing resumes automatically
                  afterward. Pair with a one-time charge to bill a different
                  amount or date inside the skipped stretch. Or pause
                  indefinitely:{' '}
                  <button
                    type="button"
                    onClick={() => handleSuspend(true, false)}
                    disabled={busy || !billingReason.trim()}
                    className="text-gold hover:underline disabled:opacity-50"
                  >
                    pause billing only
                  </button>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => handleSuspend(true, true)}
                    disabled={busy || !billingReason.trim()}
                    className="text-gold hover:underline disabled:opacity-50"
                  >
                    pause and limit access
                  </button>
                  .
                </p>
              </>
            )}
            </div>
          </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          {STAFF_ROLES.has(role ?? '')
            ? 'Staff · not billed. Facilitators and admins do not go through checkout.'
            : isZeroDues(monthlyAmount ?? info.default_amount)
              ? '$0 · no charge. Raise their monthly dues if they should start paying.'
              : 'Checkout not finished — they have not saved a card yet. Renewal date and card will show here after they complete signup.'}
        </p>
      )}

      <div>
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          <p className="text-sm font-medium text-gray-700">Charges</p>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            {([
              { id: 'upcoming', label: 'Next 6 months' },
              { id: 'history', label: historyCount ? `History (${historyCount})` : 'History' },
            ] as const).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setChargeRange(tab.id);
                  setChargeStatus('all');
                }}
                className={`px-2.5 py-1 text-xs font-medium transition ${
                  chargeRange === tab.id
                    ? 'bg-navy text-white'
                    : 'bg-white text-gray-600 hover:bg-off-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <select
            value={chargeType}
            onChange={(e) => {
              setChargeType(e.target.value as ChargeTypeFilter);
              setChargeStatus('all');
            }}
            className="px-2 py-1 border border-gray-300 rounded-lg text-xs bg-white text-gray-700 focus:ring-2 focus:ring-gold focus:border-gold"
            aria-label="Filter by charge type"
          >
            <option value="all">All types</option>
            <option value="Monthly dues">Monthly dues</option>
            <option value="One-time">One-time</option>
          </select>
          <select
            value={chargeStatus}
            onChange={(e) => setChargeStatus(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded-lg text-xs bg-white text-gray-700 focus:ring-2 focus:ring-gold focus:border-gold"
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          {allowManage && !showChargeForm && (
            <button
              type="button"
              onClick={() => setShowChargeForm(true)}
              className="text-sm text-gold hover:underline ml-auto"
            >
              + Schedule a charge
            </button>
          )}
        </div>
        {chargeRange === 'history' && info.charges_incomplete && (
          <p className="text-xs text-amber-700 mb-1.5">
            Couldn’t load full Stripe history — showing local records.
          </p>
        )}
        {allowManage && showChargeForm && (
          <div className="mb-2 rounded-lg border border-gray-200 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={chargeDate}
                onChange={(e) => setChargeDate(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold focus:border-gold"
                aria-label="Charge date"
              />
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={chargeAmount}
                  onChange={(e) => setChargeAmount(e.target.value)}
                  className="w-28 pl-6 pr-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold focus:border-gold"
                  aria-label="Charge amount"
                />
              </div>
            </div>
            <input
              type="text"
              value={chargeNote}
              onChange={(e) => setChargeNote(e.target.value)}
              placeholder="Note on their receipt (optional)"
              maxLength={200}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold focus:border-gold"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleScheduleCharge}
                disabled={busy}
                className="px-3 py-1.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition disabled:opacity-50 flex items-center gap-1.5"
              >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Schedule
              </button>
              <button
                type="button"
                onClick={() => setShowChargeForm(false)}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-navy transition"
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Auto-charges their saved card on that date (dated today, it runs
              immediately). Use this for e.g. September dues when their
              subscription doesn&apos;t start until the org billing date.
            </p>
          </div>
        )}
        {visibleRows.length === 0 ? (
          <p className="text-sm text-gray-500">
            {rangedRows.length === 0
              ? chargeRange === 'upcoming'
                ? 'No charges in the next 6 months.'
                : 'No past charges.'
              : 'No charges match these filters.'}
          </p>
        ) : (
          <HorizontalScroll className="rounded-lg border border-gray-200">
            <table className="w-full text-sm stack-table">
              <thead className="bg-off-white">
                <tr>
                  {['Date', 'Amount', 'Type', 'Status', 'Note'].map((label) => (
                    <th
                      key={label}
                      className="px-3 py-2 text-left text-xs font-semibold text-navy whitespace-nowrap"
                    >
                      {label}
                    </th>
                  ))}
                  {allowManage && (
                    <th className="px-3 py-2 text-left text-xs font-semibold text-navy">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleRows.map((row) => (
                  <tr key={row.key}>
                    <td data-label="Date" className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {row.dateLabel}
                    </td>
                    <td data-label="Amount" className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {row.amount ? `$${row.amount}` : '—'}
                    </td>
                    <td data-label="Type" className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {row.type}
                    </td>
                    <td data-label="Status" className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.statusClass}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td data-label="Note" className="px-3 py-2 text-gray-500">
                      {row.note || '—'}
                      {row.error && (
                        <p className="text-xs text-red-600 mt-0.5">{row.error}</p>
                      )}
                    </td>
                    {allowManage && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {row.cancelId != null ? (
                          <button
                            type="button"
                            onClick={() => handleCancelCharge(row.cancelId as number)}
                            disabled={busy}
                            className="text-sm text-gold hover:underline disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </HorizontalScroll>
        )}
      </div>
    </div>
  );
}
